import React, { useRef } from 'react';

import { useMutation } from '@apollo/client';
import { REMOVE_NEW_TAG_FROM_ARTICLE_MUTATION } from 'Queries/Mutations';
import { GET_ARTICLES_OVERVIEW_DATA_QUERY } from 'Queries/Queries';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Truncate from 'react-truncate';

import { LikeCard } from 'components/Article/LikeCard/LikeCard';
import { isNewEntry } from 'components/OverviewLists/utils/isNewEntry';
import { Tags } from 'components/Tags/Tags';

import { formatDate } from 'utils/DatesUtils';

import './ArticleCard.scss';

export const ArticleCard = ({ article }) => {
  const { likes, id: articleId, creationDate, title, tags, path, enableFeedback } = article;

  const checkPath = (pathCheck) => {
    if (pathCheck.startsWith('/')) {
      return pathCheck;
    }
    return `/${pathCheck}`;
  };

  const [removeNewTag] = useMutation(REMOVE_NEW_TAG_FROM_ARTICLE_MUTATION, {
    refetchQueries: () => [{ query: GET_ARTICLES_OVERVIEW_DATA_QUERY }],
    awaitRefetchQueries: true,
    variables: {
      articleId,
    },
  });

  const cardRef = useRef(null);
  const isNew = isNewEntry(tags);
  const hasManyTags = (tags?.length ?? 0) > 3;

  const cardClasses = classnames('article-card', {
    'article-card--new': isNew,
  });

  return (
    <Link to={checkPath(path)} className="article-card-link">
      <button ref={cardRef} className={cardClasses} onClick={removeNewTag}>
        <div className="article-card__header-row">
          <div className="article-card__title">
            <Truncate lines={1} ellipsis="">
              {title}
            </Truncate>
          </div>
          <div className="article-card__header-meta">
            {!hasManyTags && (
              <div className="article-card__tags">
                <Tags tags={tags} inverted={isNew} />
              </div>
            )}
            <div className="article-card__date">{formatDate(creationDate)}</div>
          </div>
        </div>
        {hasManyTags && (
          <div className="article-card__meta-row">
            <div className="article-card__tags">
              <Tags tags={tags} inverted={isNew} />
            </div>
          </div>
        )}
        <div className="article-card__footer-row">
          <LikeCard isOverview likesFromBE={likes} showLikes={enableFeedback} />
        </div>
      </button>
    </Link>
  );
};

ArticleCard.propTypes = {
  article: PropTypes.shape({
    likes: PropTypes.number,
    id: PropTypes.string.isRequired,
    creationDate: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    shortDescription: PropTypes.string,
    title: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    path: PropTypes.string.isRequired,
    enableFeedback: PropTypes.bool,
  }).isRequired,
};
